import { Toaster as SonnerToaster, toast, type ExternalToast, type ToasterProps } from 'sonner';

/**
 * Ícone do toast `loading` — lucide `loader-circle`, 24 px (Figma "Management /
 * Export jobs alignment / Loading", Sonner 16077:31061). Substitui o `Loader`
 * de barras do sonner: o wrapper dele é `position: absolute; inset: 0` e, como
 * o Toaster é `unstyled` (sem o CSS `[data-styled]` que deixa a caixa do ícone
 * relativa), o spinner escapava pelo canto superior esquerdo da caixa
 * (retorno da QA de DEV-268, item 1.3).
 */
function ToastLoadingIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-6 animate-spin text-foreground"
      aria-hidden="true"
      data-slot="toast-loader"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export function Toaster(props: ToasterProps) {
  return (
    <SonnerToaster
      position="bottom-right"
      icons={{ loading: <ToastLoadingIcon /> }}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            'flex w-[413px] max-w-[calc(100vw-2rem)] items-center gap-4 rounded-lg border border-border bg-card p-4 shadow-lg',
          content: 'flex flex-col gap-0.5 flex-1 min-w-0',
          title: 'text-sm font-medium leading-5 text-foreground',
          description: 'text-sm text-muted-foreground',
          // O sonner renderiza `[data-icon]` ANTES do conteúdo e o `.sonner-loader`
          // é absoluto: a caixa do ícone precisa ser relativa e do tamanho do
          // ícone para ele ficar DENTRO do toast.
          icon: 'relative flex size-6 shrink-0 items-center justify-center',
          // No `loading` o ícone vai para DEPOIS do texto (Figma 16077:31061:
          // "A planilha está sendo preparada" + spinner à direita).
          loading: '[&>[data-icon]]:order-last',
          actionButton:
            'inline-flex shrink-0 items-center justify-center rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          cancelButton:
            'inline-flex shrink-0 items-center justify-center rounded border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-accent',
          closeButton:
            'inline-flex size-4 items-center justify-center rounded text-muted-foreground hover:text-foreground',
        },
      }}
      {...props}
      // Diálogo modal aberto (Radix) põe `pointer-events: none` no <body>, e o
      // toaster herda: o "Fechar" de um Sonner disparado de dentro do diálogo
      // ficava inclicável, com o overlay interceptando (retorno da QA DEV-268).
      style={{ pointerEvents: 'auto', ...props.style }}
    />
  );
}

/** Id padrão do toast de erro persistente: uma nova tentativa substitui a anterior. */
export const PERSISTENT_ERROR_TOAST_ID = 'eureca-persistent-error';

export interface PersistentErrorToastOptions {
  id?: string | number;
  closeLabel?: string;
  /** Move o foco para o toast (padrão: sim). */
  focus?: boolean;
}

/**
 * Toast de erro que só sai no "Fechar" ou numa nova tentativa (mesmo `id`):
 * neutro (`toast()`, nunca `toast.error` — padrão visual do Figma), sem
 * descrição, `duration: Infinity`, `type` resetado (um update por id não herda
 * o `loading` anterior — ver EU-654 no teste) e anunciado como `role="status"`
 * com foco (DEV-261 AC-70..AC-72). O afastamento das bordas é do `<Toaster offset>`.
 */
export function persistentErrorToast(
  title: string,
  {
    id = PERSISTENT_ERROR_TOAST_ID,
    closeLabel = 'Fechar',
    focus = true,
  }: PersistentErrorToastOptions = {}
): string | number {
  const marker = `toast-persistent-${String(id).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const options: ExternalToast & { type?: undefined } = {
    id,
    duration: Infinity,
    className: marker,
    type: undefined,
    action: { label: closeLabel, onClick: () => toast.dismiss(id) },
  };
  toast(title, options);

  if (typeof window !== 'undefined') {
    const apply = (attempt: number) => {
      const el = document.querySelector<HTMLElement>(`[data-sonner-toast].${marker}`);
      if (el) {
        el.setAttribute('role', 'status');
        if (focus) el.focus();
        return;
      }
      if (attempt < 20) window.setTimeout(() => apply(attempt + 1), 16);
    };
    window.setTimeout(() => apply(0), 0);
  }
  return id;
}

export { toast };
